import { createFileRoute, Link, Outlet, redirect, useMatchRoute } from '@tanstack/react-router';
import { env } from '../../config/env';
import { cn } from '@/lib/utils';
import { HAS_BRAND_LOGO_ON_DARK } from '@/generated/platform-client';
import { isSuperAdmin } from '@/utils/auth';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useCurrentAdminUser } from '@/hooks/useCurrentAdminUser';
import { buildNavSections, type NavItem } from './nav-sections';
import { getAdminMe } from '@/api/admin/getAdminMe';
import { getTenants } from '@/api/tenants/getTenants';

import { TimezoneSelector } from '@/components/timezone/TimezoneSelector';
import { Badge } from '@/components/ui/badge';
import { AdminRole } from '@low6-pickem/api-types';

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async ({ location }) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
    const adminUser = await getAdminMe();
    const tenants = await getTenants();
    return { adminUser, tenants };
  },
  component: DashboardLayout,
});

function DashboardLayout() {
  const matchRoute = useMatchRoute();

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/#/login';
  };

  const isDev = env.ENABLE_DEV_TOOLS;
  const isMockFeed = env.FEED_PROVIDER === 'mock';
  const userIsSuperAdmin = isSuperAdmin();
  const { canAccessGameType, data: currentAdmin } = useCurrentAdminUser();

  const { data: tenantsData = [] } = useGetTenantsQuery();

  const navItems = buildNavSections({
    tenants: tenantsData,
    userIsSuperAdmin,
    canAccessGameType,
    isMockFeed,
  });

  const isActive = (to: string) => {
    if (to === '/dashboard') {
      return matchRoute({ to, fuzzy: false });
    }
    return matchRoute({ to, fuzzy: true });
  };

  const hasActiveDescendant = (item: NavItem): boolean => {
    if (item.to && isActive(item.to)) return true;
    return item.children?.some(hasActiveDescendant) ?? false;
  };

  const slugify = (value: string) => value.toLowerCase().trim().replace(/\s+/g, '-');

  const getItemKey = (item: NavItem, parentKey: string) =>
    item.to ?? `${parentKey}/${slugify(item.label)}`;

  const renderNavItem = (item: NavItem, parentKey = 'root'): React.ReactNode => {
    const itemKey = getItemKey(item, parentKey);
    const active = hasActiveDescendant(item);
    const hasChildren = (item.children?.length ?? 0) > 0;
    const to = item.to;
    const hasLink = Boolean(to);

    if (!hasChildren) {
      if (!hasLink) {
        return (
          <div
            key={itemKey}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm text-gray-600 dark:text-muted-foreground"
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        );
      }
      if (!to) return null;
      return (
        <Link
          key={itemKey}
          to={to}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded transition-colors text-sm',
            active
              ? 'text-blue-600 dark:text-primary'
              : 'text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground hover:bg-gray-50 dark:hover:bg-accent'
          )}
        >
          {item.icon}
          <span>{item.label}</span>
        </Link>
      );
    }

    const children = item.children ?? [];

    const parentLabel = to ? (
      <Link
        to={to}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded transition-colors text-sm',
          active
            ? 'text-blue-600 dark:text-primary'
            : 'text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground hover:bg-gray-50 dark:hover:bg-accent'
        )}
      >
        {item.icon}
        <span className="truncate">{item.label}</span>
      </Link>
    ) : (
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded text-sm',
          active ? 'text-blue-600 dark:text-primary' : 'text-gray-600 dark:text-muted-foreground'
        )}
      >
        {item.icon}
        <span className="truncate">{item.label}</span>
      </div>
    );

    return (
      <div key={itemKey}>
        {parentLabel}
        <div className="ml-4 mt-1 space-y-1">
          {children.map(child => renderNavItem(child, itemKey))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-background">
      <aside className="w-64 border-r bg-white dark:bg-card flex flex-col shadow-sm">
        <div className="p-4 border-b border-gray-200 dark:border-border flex flex-col items-center justify-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            {/* Generated from branding.adminLogo / branding.logoOnDark in platform.config.json
                (pnpm platform:generate), so this names no client's file.

                Two <img> elements rather than one, swapped by Tailwind's `dark:` class: an
                <img> inherits no colour from the page, and a one-colour logo filled in a
                single ink is legible on one ground and invisible on the other. The swap is
                class-based to match next-themes, which sets a class rather than relying on
                the OS preference — a prefers-color-scheme media query would disagree with the
                theme the user actually chose.

                Height, not width: the brand's clearspace and minimum sizes are defined off the
                logo's height, and the guidelines set the nav lockup at 26px. */}
            <img
              src="/brand-logo.svg"
              alt="Admin"
              className={cn('h-[26px] w-auto', HAS_BRAND_LOGO_ON_DARK && 'dark:hidden')}
            />
            {HAS_BRAND_LOGO_ON_DARK && (
              <img
                src="/brand-logo-dark.svg"
                alt=""
                aria-hidden
                className="hidden h-[26px] w-auto dark:block"
              />
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-muted-foreground text-center">
            F2P Games Platform v1.01
          </div>
        </div>
        <div className="border-b border-gray-200 dark:border-border flex flex-col items-center justify-center px-2 pb-4">
          <div className="mt-3 w-full">
            <TimezoneSelector />
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {navItems.map(item => {
            if ('items' in item) {
              // It's a section
              const sectionKey = item.label.toLowerCase().replace(/\s+/g, '-');
              const hasActiveChild = item.items.some(hasActiveDescendant);

              return (
                <div key={item.label}>
                  <div
                    className={cn(
                      'w-full flex items-center px-4 py-2 rounded text-sm font-medium uppercase tracking-wider',
                      hasActiveChild
                        ? 'bg-blue-50 dark:bg-primary/10 text-blue-600 dark:text-primary'
                        : 'text-gray-600 dark:text-muted-foreground'
                    )}
                  >
                    <span>{item.label}</span>
                  </div>
                  <div className="ml-4 mt-1 space-y-1">
                    {item.items.map(child => renderNavItem(child, sectionKey))}
                  </div>
                </div>
              );
            } else {
              // It's a regular nav item
              return renderNavItem(item, 'root');
            }
          })}
          {isDev && (
            <>
              <div className="my-2 border-t border-yellow-500/50" />
              <Link
                to="/dashboard/dev-tools"
                className={cn(
                  'block px-4 py-2 rounded transition-colors border border-yellow-500/50 text-sm',
                  matchRoute({ to: '/dashboard/dev-tools', fuzzy: true })
                    ? 'bg-yellow-100 dark:bg-yellow-950/50 text-yellow-900 dark:text-yellow-100'
                    : 'bg-yellow-50/50 dark:bg-yellow-950/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-950/50'
                )}
              >
                ⚙️ Dev Tools
              </Link>
            </>
          )}
        </nav>
        <div className="p-4 border-t border-gray-200 dark:border-border">
          {currentAdmin && (
            <div className="px-4 py-2 mb-2">
              <p className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
                {currentAdmin.name || currentAdmin.email}
              </p>
              {currentAdmin.name && (
                <p className="text-xs text-gray-500 dark:text-muted-foreground truncate">
                  {currentAdmin.email}
                </p>
              )}
              <Badge
                className={cn(
                  'mt-1 text-xs',
                  currentAdmin.role === AdminRole.SUPER_ADMIN &&
                    'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
                  currentAdmin.role === AdminRole.GAME_MANAGER &&
                    'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                  currentAdmin.role === AdminRole.MEMBER &&
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                )}
                variant="outline"
              >
                {currentAdmin.role === AdminRole.SUPER_ADMIN
                  ? 'Super Admin'
                  : currentAdmin.role === AdminRole.GAME_MANAGER
                    ? 'Game Manager'
                    : 'Member'}
              </Badge>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left rounded hover:bg-gray-50 dark:hover:bg-accent transition-colors text-sm text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground"
          >
            Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-background [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <Outlet />
      </main>
    </div>
  );
}
